import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Card, Empty, Popconfirm, Select, Skeleton, Space, Statistic, Table, Tag, Typography, message,
} from 'antd';
import type { MembershipAdminRow, MembershipRole } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Roles & access.
 * Real memberships list; change role or deactivate. Permission-key enforcement
 * per route (P-01/T-07) ships with the full Roles module; today the middleware
 * only checks "any active membership".
 */
const ROLE_LABELS: Record<MembershipRole, string> = {
  owner: 'Owner', principal: 'Principal', vice_principal: 'Vice principal',
  coordinator: 'Coordinator', teacher: 'Teacher', clerk: 'Clerk', support: 'Support',
};

export function RolesPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['memberships', institution],
    queryFn: () => productApi.listMemberships(institution),
    enabled: !!institution,
  });
  const update = useMutation({
    mutationFn: (v: { id: string; role?: MembershipRole; status?: 'active' | 'inactive' }) =>
      productApi.updateMembership(institution, v.id, { role: v.role, status: v.status }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['memberships', institution] }); },
    onError: (e) => void message.error(errMsg(e) ?? 'Update failed'),
  });

  const rows = list.data?.rows ?? [];
  const by = list.data?.by_role;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Roles &amp; access</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Every person with a membership at this institution. Change role or deactivate; the audit log
          records every change. Full permission-key enforcement (P-01) lands with the Roles module.
        </Typography.Text>
      </div>

      <Alert
        type="info"
        showIcon
        message="Enforcement is coarse right now"
        description="The middleware only checks that a valid session exists. Per-route permission checks arrive with the full Roles module; the role label already writes to audit_log for every mutation."
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Total people" value={rows.length} /></Card>
        <Card size="small"><Statistic title="Principals" value={by ? (by.owner + by.principal) : 0} valueStyle={{ color: '#553a9c' }} /></Card>
        <Card size="small"><Statistic title="Teachers" value={by?.teacher ?? 0} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Admin roles" value={by ? (by.vice_principal + by.coordinator + by.clerk) : 0} /></Card>
      </div>

      <Card size="small" title={`${rows.length} memberships`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !list.isLoading && (
          <Empty description="No memberships yet. Invitation-accept creates the first one." style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<MembershipAdminRow>
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={rows}
            columns={[
              { title: 'Name', dataIndex: 'name', key: 'n', render: (v: string) => <b>{v}</b> },
              { title: 'Email', dataIndex: 'email', key: 'e', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              {
                title: 'Role',
                dataIndex: 'role',
                key: 'r',
                width: 180,
                render: (v: MembershipRole, row) => (
                  <Select
                    size="small"
                    value={v}
                    style={{ width: 170 }}
                    disabled={row.status === 'inactive'}
                    onChange={(next) => update.mutate({ id: row.id, role: next })}
                    options={(Object.keys(ROLE_LABELS) as MembershipRole[]).map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
                  />
                ),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 's',
                width: 130,
                render: (v: string) => <Tag color={v === 'active' ? 'success' : 'default'}>{v}</Tag>,
              },
              {
                key: 'action',
                width: 160,
                align: 'right',
                render: (_, row) =>
                  row.status === 'active' ? (
                    <Popconfirm
                      title="Deactivate membership?"
                      description="They lose access immediately. Reactivation supported."
                      onConfirm={() => update.mutate({ id: row.id, status: 'inactive' })}
                      okText="Deactivate"
                      okButtonProps={{ danger: true }}
                    >
                      <a>Deactivate</a>
                    </Popconfirm>
                  ) : (
                    <a onClick={() => update.mutate({ id: row.id, status: 'active' })}>Reactivate</a>
                  ),
              },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}

function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
